/**
 * useFilterSuggestions Hook
 *
 * Generates filter suggestions based on user input with debouncing
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { GraphDataNode } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterSuggestion,
  SuggestionOptions,
} from '../types';
import { getSuggestions } from '../engine/suggestionEngine';

/**
 * Hook return type
 */
export interface UseFilterSuggestionsReturn {
  /** Current suggestions */
  suggestions: FilterSuggestion[];
  /** Whether suggestions are loading */
  isLoading: boolean;
  /** The query that generated current suggestions */
  processedQuery: string;
}

/**
 * Default debounce delay in ms
 */
const DEFAULT_DEBOUNCE_MS = 150;

/**
 * Hook for generating filter suggestions
 *
 * @param query - User's search input
 * @param nodes - All nodes for context
 * @param definitions - Available filter definitions
 * @param options - Suggestion options
 * @param debounceMs - Debounce delay (default 150ms)
 * @returns Suggestions and loading state
 */
export function useFilterSuggestions(
  query: string,
  nodes: GraphDataNode[],
  definitions: Record<string, FilterDefinition>,
  options: SuggestionOptions = {},
  debounceMs: number = DEFAULT_DEBOUNCE_MS
): UseFilterSuggestionsReturn {
  const [suggestions, setSuggestions] = useState<FilterSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [processedQuery, setProcessedQuery] = useState('');

  // Keep track of the latest query to avoid stale updates
  const latestQueryRef = useRef(query);
  latestQueryRef.current = query;

  // Memoize options to prevent unnecessary re-renders
  const memoizedOptions = useMemo(
    () => ({
      maxSuggestions: options.maxSuggestions ?? 10,
      minQueryLength: options.minQueryLength ?? 0, // Allow empty query to show filter categories
      includeFilters: options.includeFilters ?? [],
      excludeFilters: options.excludeFilters ?? [],
    }),
    [
      options.maxSuggestions,
      options.minQueryLength,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(options.includeFilters),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(options.excludeFilters),
    ]
  );

  useEffect(() => {
    const trimmedQuery = query.trim();

    setIsLoading(true);

    // Debounce the suggestion generation
    const timeoutId = setTimeout(() => {
      // Only process if this is still the latest query
      if (latestQueryRef.current.trim() !== trimmedQuery) {
        return;
      }

      try {
        const newSuggestions = getSuggestions(
          trimmedQuery,
          definitions,
          nodes,
          memoizedOptions
        );

        // Only update if this is still the latest query
        if (latestQueryRef.current.trim() === trimmedQuery) {
          setSuggestions(newSuggestions);
          setProcessedQuery(trimmedQuery);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error generating suggestions:', error);
        setSuggestions([]);
        setIsLoading(false);
      }
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [query, nodes, definitions, memoizedOptions, debounceMs]);

  return useMemo(
    () => ({
      suggestions,
      isLoading,
      processedQuery,
    }),
    [suggestions, isLoading, processedQuery]
  );
}
